import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {Store} from '@ngrx/store';
import {Subscription, take} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {ALLOWED_IMAGE_TYPES, IMAGE_UPLOAD_MAX_SIZE} from 'src/app/app.constants';
import {selectActiveUser} from 'src/app/core/store/active-user/active-user.selector';
import {DeleteImageAction} from 'src/app/core/store/active-user/active-user.action';
import {FileUploadService} from 'src/app/services/file-upload.service';
import {MessageService} from 'src/app/services/message.service';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';

@Component({
  selector: 'current-sked-metric-photo-upload',
  standalone: true,
  imports: [MatIconModule, MatProgressSpinnerModule, OphXButtonComponent],
  templateUrl: './current-sked-metric-photo-upload.component.html',
  styleUrl: './current-sked-metric-photo-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentSkedMetricPhotoUploadComponent implements OnInit, OnDestroy {
  currentValue = input('');
  metricName = input('');
  valueChange = output<string>();
  uploadStateChange = output<boolean>();

  private readonly store$ = inject(Store);
  private readonly fileUploadService = inject(FileUploadService);
  private readonly messageService = inject(MessageService);

  uploadProgress = signal(0);
  uploadLoading = signal(false);
  imageLoading = signal(false);

  showUploadButton = computed(() => !this.currentValue() && !this.uploadLoading());
  showProgress = computed(() => this.uploadLoading());
  showImageLoading = computed(() => this.currentValue() && !this.uploadLoading() && this.imageLoading());
  showPreview = computed(() => this.currentValue() && !this.uploadLoading());

  selectedFile: File | null = null;
  tempImageUrl = '';
  deletedImgs: string[] = [];
  allowedImageTypes = ALLOWED_IMAGE_TYPES;
  maxFileSize = IMAGE_UPLOAD_MAX_SIZE;

  private uploadSubscription?: Subscription;
  private boundCheckDeletedImgs = this.checkDeletedImgs.bind(this);
  private boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

  ngOnInit() {
    // Add event listeners for page refresh/close/hide
    window.addEventListener('beforeunload', this.boundCheckDeletedImgs);
    window.addEventListener('pagehide', this.boundCheckDeletedImgs);
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);

    // If there's already an image URL, start in loading state
    if (this.currentValue()) {
      this.imageLoading.set(true);
    }
  }

  ngOnDestroy() {
    // Remove event listeners
    window.removeEventListener('beforeunload', this.boundCheckDeletedImgs);
    window.removeEventListener('pagehide', this.boundCheckDeletedImgs);
    document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);

    this.uploadSubscription?.unsubscribe();

    if (this.tempImageUrl) {
      URL.revokeObjectURL(this.tempImageUrl);
    }

    this.checkDeletedImgs();
  }

  private handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.checkDeletedImgs();
    }
  }

  onUploadPhoto() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = this.allowedImageTypes.join(',');
    input.onchange = (event: Event) => this.onFileSelected(event);
    input.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      const validationError = this.fileUploadService.validateFile(file);
      if (validationError) {
        this.messageService.add(validationError);
        return;
      }

      this.uploadLoading.set(true);
      this.uploadStateChange.emit(true);
      this.selectedFile = file;
      this.tempImageUrl = URL.createObjectURL(file);

      this.store$
        .select(selectActiveUser)
        .pipe(take(1))
        .subscribe(user => {
          this.uploadSubscription = this.fileUploadService.uploadFile(file, `${user._id}/`).subscribe({
            next: result => {
              this.uploadProgress.set(result.progress);
              if (result.url) {
                this.onUploadSuccess(result.url);
              }
            },
            error: (error: HttpErrorResponse) => {
              this.onUploadFailure(error);
            },
          });
        });
    }
  }

  onUploadSuccess(url: string) {
    this.valueChange.emit(url);
    this.uploadLoading.set(false);
    this.uploadProgress.set(0);
    this.uploadStateChange.emit(false);
    this.uploadSubscription = undefined;
    this.imageLoading.set(true); // Start image loading state
  }

  onUploadFailure(err: HttpErrorResponse) {
    this.messageService.add(err.message || 'There was a problem uploading the image.');
    this.uploadLoading.set(false);
    this.uploadProgress.set(0);
    this.uploadStateChange.emit(false);
    this.uploadSubscription = undefined;

    if (this.tempImageUrl) {
      URL.revokeObjectURL(this.tempImageUrl);
      this.tempImageUrl = '';
    }
  }

  onImageLoad() {
    this.imageLoading.set(false);

    if (this.tempImageUrl) {
      URL.revokeObjectURL(this.tempImageUrl);
      this.tempImageUrl = '';
    }
  }

  onImageError() {
    this.imageLoading.set(false);
    this.messageService.add('Failed to load image');
  }

  onDeletePhoto() {
    if (this.currentValue()) {
      const ids = encodeURIComponent(this.currentValue());
      this.deletedImgs.push(ids);
      this.checkDeletedImgs();
    }
    this.imageLoading.set(false);
    this.valueChange.emit('');
  }

  checkDeletedImgs() {
    if (this.deletedImgs.length) {
      const ids = this.deletedImgs.join(',');
      this.store$.dispatch(
        new DeleteImageAction({
          ids,
        })
      );
      this.deletedImgs = [];
    }
  }
}
